import { useState, useCallback } from 'react';
import { ButtonType } from '@ampol/design-system';
import { trackCustomEvent } from 'utilities/app-insights';

/**
 * Hook for managing scheduled maintenance modal state and interactions
 *
 * Responsibilities:
 * - Modal open/close state
 * - Edit vs. Create mode
 * - Form validation state
 * - Provides configuration for modal (title, actions, topActions)
 * - GTM tracking for modal interactions
 *
 * Note: This hook returns configuration that the parent component uses with useModal.
 * The parent component handles the useModal call and JSX content rendering.
 */

interface UseScheduledMaintenanceModalsProps {
  scheduledMaintenance: any;
  scheduledMaintenanceLoading: boolean;
  scheduledMaintenanceError: any;
  createScheduledMaintenanceLoading: boolean;
  createScheduledMaintenanceLoaded: boolean;
  updateScheduledMaintenanceLoading: boolean;
  deleteScheduledMaintenanceLoading: boolean;
  loggedInUser: any;
  assignedAccountNumber: string;
  onDelete: () => void;
}

export const useScheduledMaintenanceModals = ({
  scheduledMaintenance,
  scheduledMaintenanceLoading,
  scheduledMaintenanceError,
  createScheduledMaintenanceLoading,
  createScheduledMaintenanceLoaded,
  updateScheduledMaintenanceLoading,
  deleteScheduledMaintenanceLoading,
  loggedInUser,
  assignedAccountNumber,
  onDelete,
}: UseScheduledMaintenanceModalsProps) => {
  const [isEditSchedule, setIsEditSchedule] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [isSubmitValid, setIsSubmitValid] = useState(false);

  const handleAddEditScheduleStateChange = useCallback(({ isValid }: { isValid: boolean }) => {
    setIsSubmitValid(isValid);
  }, []);

  const handleOpenAddModal = useCallback(() => {
    setIsEditSchedule(false);
    setShowModal(true);

    trackCustomEvent(
      'Open Add Scheduled Maintenace Modal',
      loggedInUser,
      assignedAccountNumber,
      'Clicked Add Scheduled Maintenance button.'
    );
  }, [loggedInUser, assignedAccountNumber]);

  const handleOpenEditModal = useCallback(() => {
    setIsEditSchedule(true);
    setShowModal(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    trackCustomEvent(
      'Close Add/Edit Scheduled Maintenance Modal',
      loggedInUser,
      assignedAccountNumber,
      'Closed Add or Edit Scheduled Maintenance modal.'
    );
    setShowModal(false);
  }, [loggedInUser, assignedAccountNumber]);

  // Modal configuration that parent component will use
  const getModalConfig = useCallback(
    () => ({
      id: 'scheduled-maintenance-modal',
      title: isEditSchedule ? 'Edit Schedule' : 'Create Schedule',
      formId: 'scheduled-maintenance-form',
      actions: [
        {
          label: 'Save',
          type: createScheduledMaintenanceLoaded ? ButtonType.Button : ButtonType.Submit,
          inactive:
            !isSubmitValid ||
            createScheduledMaintenanceLoading ||
            updateScheduledMaintenanceLoading ||
            deleteScheduledMaintenanceLoading,
        },
        {
          label: 'Cancel',
          onClick: () => setShowModal(false),
        },
      ],
      topActions: isEditSchedule
        ? [
            {
              label: 'Delete',
              onClick: onDelete,
              inactive:
                scheduledMaintenanceLoading ||
                scheduledMaintenanceError ||
                deleteScheduledMaintenanceLoading ||
                createScheduledMaintenanceLoading ||
                updateScheduledMaintenanceLoading,
            },
          ]
        : [],
      onClose: handleCloseModal,
    }),
    [
      isEditSchedule,
      createScheduledMaintenanceLoaded,
      isSubmitValid,
      createScheduledMaintenanceLoading,
      updateScheduledMaintenanceLoading,
      deleteScheduledMaintenanceLoading,
      scheduledMaintenanceLoading,
      scheduledMaintenanceError,
      onDelete,
      handleCloseModal,
    ]
  );

  return {
    showModal,
    setShowModal,
    isEditSchedule,
    isSubmitValid,
    handleOpenAddModal,
    handleOpenEditModal,
    handleAddEditScheduleStateChange,
    getModalConfig,
  };
};
